/**
 * Entra ID sign-in.
 *
 * Authorization code flow with PKCE, via MSAL.
 *
 * On storage: `sessionStorage`, deliberately, and **not** `memoryStorage`. The redirect
 * flow navigates the whole page to Microsoft and back, and the PKCE verifier and nonce
 * have to survive that round trip. In-memory storage cannot, so MSAL refuses the
 * redirect outright with `in_mem_redirect_unavailable` — sign-in is impossible by
 * construction. That is exactly what shipped first, and the failure was silent.
 *
 * `sessionStorage` is scoped to the one tab and cleared when it closes, so a token never
 * outlives the session and is never shared with another tab or origin. The platform
 * standard's rule is not to persist tokens in `localStorage`; this satisfies it.
 */

import {
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
  type SilentRequest,
} from '@azure/msal-browser'

const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID as string | undefined
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID as string | undefined

if (!tenantId || !clientId) {
  throw new Error(
    'VITE_ENTRA_TENANT_ID and VITE_ENTRA_CLIENT_ID must be set. See web/.env.example.',
  )
}

/** The scope the API requires. A token without it is rejected server-side. */
export const API_SCOPE = `api://${clientId}/access_as_user`

const configuration: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // Scoped to this tab and cleared when it closes. Never localStorage, which would
    // persist a token across sessions and share it between tabs.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
}

export const msal = new PublicClientApplication(configuration)

export const loginRequest = { scopes: [API_SCOPE] }

/**
 * An access token for the API.
 *
 * Silent first; interactive only when the silent path genuinely cannot succeed, so the
 * user is not bounced to a sign-in page for an expired token that MSAL could renew.
 */
export async function acquireToken(): Promise<string | null> {
  const account: AccountInfo | null = msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null
  if (!account) return null

  const request: SilentRequest = { ...loginRequest, account }

  try {
    const result = await msal.acquireTokenSilent(request)
    return result.accessToken
  } catch {
    // Consent required, or the session has genuinely expired. A redirect is the only
    // way forward; returning null would leave the caller retrying a request that can
    // never succeed.
    await msal.acquireTokenRedirect(request)
    return null
  }
}
